import React from "react";
import styled from "styled-components";
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
    Card as MuiCard,
    CardContent as MuiCardContent,
    CardHeader,
    useTheme,
    Box
} from "@mui/material";
import { spacing } from "@mui/system";
import { chartColors } from "../config/analyticsCharts";
import EmptyState from "../common/EmptyState";

// One height for the chart and for the state that stands in for it, so an empty card is
// exactly as tall as the chart it replaces and the tab does not jump as data arrives.
const CHART_HEIGHT = 400;
import { getDistinctCategoryColors, getDistributedBarColors } from "../utils/categoryColors";
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import { t } from "../../../../utils/translate";
import { DEFAULT_CURRENCY, formatCompactMoney, formatFullMoney } from "../utils/moneyFormat";
import { markIncompleteMonths } from "../utils/periodLabels";

const defaultColors = chartColors.seriesColors;

// SHER-846. An appointment whose agenda resolves to no department makes three analytics
// endpoints emit that department's name as JSON `null`, so `categories` arrives typed
// `string[]` and holding a null. ApexCharts sizes its axis with
// `getLargestStringFromArr`, which reduces the labels using `a.length > b.length ? a : b`
// (node_modules/apexcharts/src/utils/Utils.js) -- `null.length` throws, the chart aborts
// after its legend is in the DOM and before the plot is drawn, and the panel renders as
// an empty rectangle under a title and a total that both look fine. Four Outpatients
// panels were blank on cardio24 and CHOM Dakar for exactly this.
//
// An empty string does not throw (`''.length === 0`) but draws a nameless axis tick, so
// it gets the same label: an axis entry the reader cannot name is not a rendered chart.
const categoryLabel = (value: unknown, fallback: string): string =>
    (value != null ? String(value) : '') || fallback;

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)`
  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(4)};
  }
`;

interface BarChartProps {
    series: {
        name: string,
        data: number[],
        // Set when the series has a MEANING that a palette must not override -- honoured
        // green, no-show red, a wait over a month red. Series names are translated at
        // runtime, so they miss categoryColorMap and fall through to the hash: without
        // this, a French panel drew honoured and non-honoured in two similar greens.
        color?: string
    }[],
    title: React.ReactNode,
    // Nullable by contract: three analytics endpoints emit an unresolved department as
    // `null` (SHER-846). Typed so a consumer that forgets to normalise fails to compile.
    categories: (string | null)[],
    type?: 'bar' | 'line' | 'area' | 'radar' | 'scatter' | 'heatmap' | 'pie' | 'donut',
    stacked?: boolean,
    horizontal?: boolean
    total?: number,
    ditributed?: boolean,
    patientIds?: Record<string, number[]>,
    // SHER-853. The Finances series are amounts of money, and nothing on the card said so:
    // the axis printed `60000000` under a header that showed a document COUNT. These three
    // are opt-in per chart (set in config/analyticsCharts.ts and forwarded by ChartFactory)
    // rather than inferred, because "is this series money?" is not answerable from a
    // number[] -- 45235300 could be an amount, a patient id or a duration in ms.
    money?: boolean,
    currency?: string,
    // Translation id naming what `total` counts, so the header figure carries its unit.
    totalUnitId?: string,
    markIncompletePeriod?: boolean,
    // Cap on the number of pie/donut slices. A clinic carries a long tail of insurers and
    // the chart drew one sliver per insurer with a legend taller than itself. Opt-in per
    // chart in config/analyticsCharts.ts: most pies have few enough categories to show
    // whole, and truncating those would hide data for no gain.
    topN?: number
}

const EnhancedChart = ({ 
    series, 
    title, 
    categories, 
    type = 'bar',
    stacked = false,
    horizontal = false,
    total,
    ditributed = false,
    patientIds,
    money = false,
    currency = DEFAULT_CURRENCY,
    totalUnitId,
    markIncompletePeriod = false,
    topN
}: BarChartProps) => {
    const theme = useTheme();
    // Deliberately NOT the t() helper used further down this file: that one reads the
    // language out of localStorage, so a label built with it keeps whatever language the
    // page was loaded in and never follows the language switcher. The remainder slice is
    // new, so it starts on the mechanism that reacts.
    //
    // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from
    // the package's typings. See analyticsTranslate.sher828.test.tsx.
    const { translate } = React.useContext(LocalizeContext as React.Context<LocalizeContextProps>);
    const otherLabel = translate('hospital.analytics.chart.other') as string;
    // SHER-846. Same mechanism as the remainder slice above, and the same reason for using
    // `translate` rather than t(). Deliberately domain-neutral: this component draws finance
    // months and insurance series too, so a wording naming a *department* would be false on
    // most of the axes it labels.
    const unspecifiedLabel = translate('hospital.analytics.chart.unspecified') as string;

    // SHER-846: normalised once, here, because `categories` has three consumers — the pie
    // labels, the distributed-bar colours and the x-axis — and normalising per consumer is
    // how one of them gets missed. Everything downstream reads this, never the raw prop.
    const namedCategories = React.useMemo(
        () => (Array.isArray(categories) ? categories.map((c) => categoryLabel(c, unspecifiedLabel)) : categories),
        [categories, unspecifiedLabel],
    );

    // If we have patient IDs, we need to calculate gender-based data
    React.useEffect(() => {
        if (patientIds && (type === 'bar' || type === 'line') && series && Array.isArray(series) && series.length === 3) {
            // This is our diagnosis chart with Female, Male, Total series
            // We'd need to fetch patient data here and update the series data
            // For now, this will be handled in the parent component
        }
    }, [patientIds, series, type]);

    // Function to sum data and get labels for pie/donut
    const getSummedSeriesDataForPie = (series: { name: string, data: number[] }[] | undefined, categories?: string[]) => {
        if (!series || !Array.isArray(series) || series.length === 0) {
            return { values: [], labels: [] };
        }
        if (categories && series.length === 1) {
            // Handle single series with categories (like your insurance data)
            // Already normalised by the caller. The multi-series branch below is NOT
            // categories - those labels are series names, which keep their own coercion.
            return {
                values: series[0]?.data || [],
                labels: categories || [],
            };
        } else {
            // Handle multiple series (original behavior)
            const values = series.map((s) => (s?.data || []).reduce((a, b) => a + b, 0));
            const labels = series.map((s) => s?.name || '');
            return { values, labels };
        }
    };

    const isPieOrDonut = ['pie', 'donut'].includes(type);

    // Process data and determine labels/colors based on chart type
    const { chartSeries, chartLabels, finalColors } = React.useMemo(() => {
        let processedSeries: ApexAxisChartSeries | ApexNonAxisChartSeries = [];
        let processedLabels: string[] | undefined = undefined;
        let calculatedColors: string[] = [...defaultColors]; // Start with default

        if (isPieOrDonut) {
            if (!series || !Array.isArray(series)) {
                processedSeries = [];
                processedLabels = [];
            } else {
                const data = getSummedSeriesDataForPie(series, namedCategories);
                if (!data || !data.labels || !Array.isArray(data.labels) || !data.values || !Array.isArray(data.values)) {
                    processedSeries = [];
                    processedLabels = [];
                } else {
                const sorted = data.labels
                    .map((label, index) => ({ 
                        // The fallback was already applied where the labels are categories
                        // (getSummedSeriesDataForPie); series names keep their own '' coercion.
                        label: (label != null ? String(label) : '') || '',
                        value: (data.values && data.values[index] != null) ? data.values[index] : 0 
                    }))
                    .filter(item => item != null)
                    .sort((a, b) => {
                        const labelA = (a?.label != null ? String(a.label) : '') || '';
                        const labelB = (b?.label != null ? String(b.label) : '') || '';
                        return labelA.localeCompare(labelB);
                    });

                    let slices = sorted.map(item => ({
                        label: item?.label ?? '',
                        value: item?.value ?? 0,
                    }));

                    // Cap by SIZE, not by the alphabetical order above: the point is to
                    // keep the slices that carry the volume. The tail is summed into one
                    // labelled slice rather than dropped, so the pie still totals what
                    // the card claims.
                    if (topN && topN > 0 && slices.length > topN) {
                        const bySize = [...slices].sort((a, b) => b.value - a.value);
                        const kept = bySize.slice(0, topN);
                        const remainder = bySize.slice(topN).reduce((sum, s) => sum + s.value, 0);
                        slices = [...kept, { label: otherLabel, value: remainder }];
                    }

                    processedSeries = slices.map(item => item.value); // number[] for pie/donut series
                    processedLabels = slices.map(item => item.label); // string[] for pie/donut labels
                }
            }
            
            // Calculate colors based on category names
            // SHER-846: blank the unresolved slot so getCategoryColor's falsy guard gives it
            // the default blue it has always had. Colouring from the translated label instead
            // would make it purple in EN, amber in ES and brown in FR - and the amber already
            // belongs to a real department, so the clash would cascade to a sibling.
            calculatedColors = processedLabels
                ? getDistinctCategoryColors(processedLabels.map((l) => (l === unspecifiedLabel ? '' : l)))
                : [];
        } else {
            // For other charts, sort series by name
            if (!series || !Array.isArray(series)) {
                processedSeries = [];
                calculatedColors = [];
            } else {
                const sortedSeries = series.map(s => ({
                    name: s?.name || '',
                    data: s?.data || [],
                    color: s?.color
                })).filter(s => s != null).sort((a, b) => {
                    const nameA = (a?.name != null ? String(a.name) : '') || '';
                    const nameB = (b?.name != null ? String(b.name) : '') || '';
                    return nameA.localeCompare(nameB);
                });
                
                // apexcharts reads `colors` positionally against the series it is given,
                // and the sort above reorders them, so the explicit colours are picked out
                // AFTER sorting rather than in the caller's order.
                processedSeries = sortedSeries.map(({ name, data }) => ({ name, data }));

                if (ditributed && namedCategories && namedCategories.length > 0) {
                    // Same blanking as the pie branch above, and for the same reason.
                    calculatedColors = getDistributedBarColors(
                        namedCategories.map((l) => (l === unspecifiedLabel ? '' : l)),
                    );
                } else {
                    // SHER-853: per-name lookup cannot see siblings, so three series all
                    // starting with "T" (Total Billing / Total Discounts / Total Income)
                    // hashed to the same red. Resolve the whole set at once.
                    const derived = getDistinctCategoryColors(sortedSeries.map(s => s.name));
                    // A caller that named a colour meant it: honoured is green because it is
                    // honoured, not because of where its label sorts. Only the series that
                    // named none fall back to the derived palette.
                    calculatedColors = sortedSeries.map((s, i) => s.color || derived[i]);
                }
            }
        }

        return { chartSeries: processedSeries, chartLabels: processedLabels, finalColors: calculatedColors };
    }, [series, categories, isPieOrDonut, ditributed, topN, otherLabel, unspecifiedLabel]);

    const hideLegend = ditributed && type === 'bar' && !horizontal;

    // SHER-853. Three places print a series value -- the bar's own label, the axis tick and
    // the tooltip -- and until now all three shared one formatter that did nothing but pick
    // a number of decimals. That is why the Finances axis read `60000000`.
    //
    // The axis and the bar label abbreviate, because they are read at a glance in about
    // sixty pixels; the tooltip is opened deliberately and stays exact to the franc.
    // Percentages (pie/donut) keep the behaviour they already had.
    const formatValue = React.useCallback((value: any, mode: 'axis' | 'point' | 'exact') => {
        if (typeof value !== 'number') {
            return String(value);
        }
        // Percent only where it was before: the bar label and the tooltip. The y-axis
        // formatter never carried this branch, and a pie has no y-axis to put it on.
        if ((type === 'pie' || type === 'donut') && mode !== 'axis') {
            return value.toFixed(1) + '%';
        }
        if (money) {
            if (mode === 'exact') {
                return formatFullMoney(value, currency);
            }
            // A bar's own label sits inside a column about forty pixels wide: "45.2M CFA"
            // overflows it and gets clipped mid-word. The currency is on the axis two
            // centimetres to the left, so the point label drops it and keeps the figure.
            return mode === 'axis'
                ? formatCompactMoney(value, currency)
                : formatCompactMoney(value, '').trimEnd();
        }
        if (value % 1 === 0) {
            return value.toString();
        }
        return value < 1 ? value.toFixed(2) : value.toFixed(1);
    }, [type, money, currency]);

    // The month at the end of the range has not closed yet, so its bucket is a partial
    // figure by construction. On the Total Billing line that read as revenue collapsing
    // rather than as a month still in progress.
    const chartCategories = React.useMemo(
        () => (markIncompletePeriod
            ? markIncompleteMonths(namedCategories, t('hospital.analytics.chart.partial_period'))
            : namedCategories),
        [namedCategories, markIncompletePeriod],
    );

    const chartOptions: ApexOptions = React.useMemo(() => ({
        chart: {
            background: 'transparent',
            stacked: stacked,
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                }
            },
            zoom: {
                enabled: true
            }
        },
        plotOptions: {
            bar: {
                horizontal: horizontal,
                columnWidth: '80%',
                borderRadius: 4,
                distributed: ditributed,
                enableShades: true,
                shadeIntensity: 0.5,
                reverseNegativeShade: true,
                reversePositiveShade: true,
            },
        },
        colors: finalColors,
        dataLabels: {
            enabled: true,
            formatter: (val: any) => formatValue(val, 'point')
        },
        fill: {
            type: type === 'area' ? 'gradient' : 'solid',
            opacity: type === 'area' ? [0.35, 0.25, 0.2] : 1,
        },
        grid: {
            show: true,
            borderColor: theme.palette.divider,
            strokeDashArray: 3,
            position: 'back' as const,
        },
        legend: {
            show: !hideLegend,
            position: 'bottom',
            horizontalAlign: 'center',
            floating: false,
            offsetY: 0,
            offsetX: 0,
            fontSize: '16px',
            labels: {
                colors: theme.palette.text.secondary,
                useSeriesColors: false
            },
            markers: {
                size: 8,
                strokeWidth: 0,
                shape: 'circle' as const
            }
        },
        stroke: {
            curve: 'straight',
            width: type === 'bar' ? 0 : 2,
        },
        theme: {
            mode: theme.palette.mode
        },
        tooltip: {
            shared: true,
            intersect: false,
            theme: theme.palette.mode,
            y: {
                formatter: (value: any) => formatValue(value, 'exact')
            }
        },
        xaxis: {
            type: 'category',
            categories: chartCategories,
            axisBorder: {
                show: true,
                color: theme.palette.divider,
            },
            axisTicks: {
                show: true,
                color: theme.palette.divider,
            },
            labels: {
                style: {
                    colors: theme.palette.text.secondary,
                    fontSize: '14px'
                },
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: theme.palette.text.secondary,
                    fontSize: '14px'
                },
                formatter: (value: any) => formatValue(value, 'axis')
            }
        },
        // Special settings for pie/donut charts
        ...(isPieOrDonut && {
            labels: chartLabels,
            pie: {
                expandOnClick: true,
                donut: {
                    size: '70%',
                    labels: {
                        show: true,
                        name: {
                            show: true
                        },
                        value: {
                            show: false // Changed from true to false to hide individual values
                        },
                        total: {
                            show: false, // Changed from true to false to hide the center value
                            label: t('general.total'), // Use the translation from global.json
                            formatter: function (w: any) {
                                return w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                            }
                        }
                    },
                }
            }
        })
    }), [stacked, horizontal, ditributed, hideLegend, theme, chartCategories, chartLabels, finalColors, isPieOrDonut, type, formatValue]);

    // Nothing to plot is an ordinary state -- a new tenant, a quiet week, a filter that
    // matches nothing -- and every analytics endpoint answers 200 with an empty series for
    // it. ApexCharts does not tolerate that: handed an empty series it throws
    // `Cannot read properties of undefined (reading 'length')`, which on beta/chom meant
    // eight uncaught exceptions and a blank Laboratory tab (SHER-832).
    //
    // Decided on the PROP, not on the derived chartSeries. The pie/donut branch of the memo
    // above turns [{name:'X',data:[]}] plus categories into [0, 0], so a check downstream of
    // it cannot tell "no data" from "measured zero" and would mount a pie of zeros for the
    // exact shape this renders as empty for a bar. Summation cannot create data from
    // nothing, so the raw prop answers the question for every chart type at once.
    //
    // Checked on the DATA and not on the series count, because an endpoint with no rows
    // still returns its named series -- location answers [{name:'Inpatient',data:[]}] -- and
    // a guard reading only `series.length` would wave that straight through.
    //
    // Open question, deliberately left open rather than guessed at: apexcharts ^3.36.3 has a
    // `noData: { text }` option that may cover these shapes on its own, which would replace
    // this guard with one key in the options memo. Deciding it needs a real browser -- the
    // test below mocks the library precisely because it cannot mount under jsdom -- so it was
    // not settled here. Whoever spikes it should record the answer, either way, right here.
    const hasData = Array.isArray(series)
        && series.some((entry) => Array.isArray(entry?.data) && entry.data.length > 0);

    // SHER-853, defect 1. `total` is whatever the endpoint put in chartData.count, and for
    // finances that is a DOCUMENT COUNT: getTotalBilling sets it to totalBills, so the card
    // printed "Total: 403" directly above a chart plotting 52 703 227. Neither figure
    // carried a unit, so the reader was handed two numbers that cannot both be "the total"
    // and no way to tell which was which.
    //
    // Naming what the count counts is what removes the ambiguity -- "Invoices: 403" cannot
    // be read as money. The amount is added next to it, and it is summed from the series
    // actually rendered rather than from a second field of the payload, so the header and
    // the chart cannot drift apart. Only for a single-series chart: on the three-series
    // discounts card there is no one amount the card is "the total" of, and the per-series
    // figures are on the axis and in the tooltip.
    const subheader = React.useMemo(() => {
        const parts: string[] = [];
        if (typeof total === 'number' && total > 0 && totalUnitId) {
            parts.push(`${t(totalUnitId)}: ${total}`);
        } else if (total && total > 1) {
            // Every non-finances chart keeps the header it had.
            parts.push(`Total: ${total}`);
        }
        if (money && Array.isArray(series) && series.length === 1) {
            const amount = (series[0]?.data || []).reduce((sum, value) => sum + (Number(value) || 0), 0);
            parts.push(formatFullMoney(amount, currency));
        }
        return parts.length ? parts.join(' · ') : null;
    }, [total, totalUnitId, money, series, currency]);

    return (
        <Card>
            <CardHeader 
                title={title}
                titleTypographyProps={{ fontWeight: 600 }}
                subheader={subheader} 
            />
            <CardContent>
                <Box>
                    {hasData ? (
                        <Chart
                            options={chartOptions}
                            series={chartSeries}
                            type={type}
                            height={CHART_HEIGHT}
                        />
                    ) : (
                        <EmptyState height={CHART_HEIGHT} />
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};


export default EnhancedChart;
